// Gallery Scroll Effect using GSAP ScrollTrigger
// Pins the product container and smoothly scrolls through gallery images
class GalleryScrollEffect {
  constructor() {
    this.galleryContainer = document.querySelector('.product-gallery__container');
    this.galleryItems = document.querySelectorAll('.product-gallery__item');
    this.productSection = document.querySelector('.main-product');
    this.infoSection = document.querySelector('.main-product__info');
    this.container = document.querySelector('.main-product__container');
    
    if (!this.galleryContainer || this.galleryItems.length === 0 || !this.container) {
      return;
    }
    
    // Wait for GSAP and ScrollTrigger to be available
    this.init();
  }
  
  init() {
    // Only run when the product layout is side by side
    if (window.innerWidth < 1025) {
      return;
    }
    
    // Check if GSAP and ScrollTrigger are available
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.log('Waiting for GSAP libraries to load...');
      setTimeout(() => this.init(), 50);
      return;
    }
    
    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);
    
    const itemCount = this.galleryItems.length;
    const viewportHeight = window.innerHeight;
    
    // Calculate total height needed for gallery container
    // Each item should be viewport height
    const totalGalleryHeight = itemCount * viewportHeight;
    this.galleryContainer.style.height = `${totalGalleryHeight}px`;
    
    // Ensure each gallery item is viewport height
    this.galleryItems.forEach(item => {
      item.style.height = `${viewportHeight}px`;
    });
    
    // Calculate the scroll distance needed
    // This is the distance the user needs to scroll to go through all images
    // Each image transition should take about 1 viewport height of scroll
    const scrollDistance = (itemCount - 1) * viewportHeight;
    
    // Create a single ScrollTrigger that handles both pinning and animation
    // This ensures smooth coordination between pinning and gallery scrolling
    const scrollTriggerConfig = {
      trigger: this.container,
      start: 'top top',
      end: `+=${scrollDistance}`,
      pin: true,
      pinSpacing: true,
      anticipatePin: 1,
      scrub: true, // Smooth animation tied to scroll position
      onUpdate: (self) => {
        // Update info section state based on progress
        const progress = self.progress;
        if (progress >= 1) {
          this.infoSection?.classList.add('gallery-scroll-complete');
        } else {
          this.infoSection?.classList.remove('gallery-scroll-complete');
        }
      },
      onLeave: () => {
        // When leaving the pinned section (scrolled past)
        this.infoSection?.classList.add('gallery-scroll-complete');
      },
      onEnterBack: () => {
        // When scrolling back into the pinned section
        this.infoSection?.classList.remove('gallery-scroll-complete');
      }
    };
    
    // Animate the gallery container to scroll through images
    // Progress goes from 0 to 1 as user scrolls through the pinned section
    const animation = gsap.to(this.galleryContainer, {
      y: -(totalGalleryHeight - viewportHeight), // Move up by (total height - one viewport)
      ease: 'none',
      scrollTrigger: scrollTriggerConfig
    });
    
    // Store reference for cleanup if needed
    this.scrollTrigger = animation.scrollTrigger;
    
    // Handle resize events
    let resizeTimer;
    const resizeHandler = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const newViewportHeight = window.innerHeight;
        const newTotalHeight = itemCount * newViewportHeight;
        const newScrollDistance = (itemCount - 1) * newViewportHeight;
        
        // Update heights
        this.galleryContainer.style.height = `${newTotalHeight}px`;
        this.galleryItems.forEach(item => {
          item.style.height = `${newViewportHeight}px`;
        });
        
        // Only update if still on desktop
        if (window.innerWidth < 1025) {
          return;
        }
        
        // Update the animation
        if (this.scrollTrigger) {
          // Update the end position
          this.scrollTrigger.vars.end = `+=${newScrollDistance}`;
          // Update the animation target value
          const animation = gsap.getTweensOf(this.galleryContainer)[0];
          if (animation) {
            animation.vars.y = -(newTotalHeight - newViewportHeight);
          }
        }
        
        // Refresh ScrollTrigger to recalculate positions
        ScrollTrigger.refresh();
      }, 250);
    };
    
    window.addEventListener('resize', resizeHandler);
    this.resizeHandler = resizeHandler; // Store for cleanup
  }
  
  // Cleanup method (optional, for when component is destroyed)
  destroy() {
    if (this.scrollTrigger) {
      this.scrollTrigger.kill();
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.vars.trigger === this.container) {
        trigger.kill();
      }
    });
  }
}

// Initialize when DOM and GSAP are ready
function initGalleryScrollEffect() {
  const domReady = document.readyState === 'complete' || document.readyState === 'interactive';
  const gsapReady = typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';
  
  if (domReady && gsapReady) {
    // Wait a bit longer if ScrollSmoother is being used to ensure it's initialized
    // ScrollSmoother should be compatible with ScrollTrigger, but we want to make sure
    const delay = typeof ScrollSmoother !== 'undefined' && window.scrollSmoother ? 200 : 100;
    
    setTimeout(() => {
      new GalleryScrollEffect();
      // Refresh ScrollTrigger after initialization to ensure proper positioning
      // This is especially important if ScrollSmoother is active
      ScrollTrigger.refresh();
    }, delay);
  } else {
    setTimeout(initGalleryScrollEffect, 50);
  }
}

// Start initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGalleryScrollEffect);
} else {
  initGalleryScrollEffect();
}
